import { useState } from 'react';
import '@aws-amplify/ui-react/styles.css';
import LivenessQuickStartReact from './components/AWSLivenessComponent';
import { useWakeLock } from './hooks/useWakeLock';
import { useTranslation } from './hooks/useTranslation';

const CameraSlashIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="m3 3 18 18M9.5 6H7a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-3.5L16.5 9H14l-2-3H9.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M10 11.5a2.5 2.5 0 0 0 2.5 2.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

// V2 flow: own pre-check screen with a custom Start button; the SDK is only
// mounted after the user taps, with disableStartScreen so its own banner and
// button never render. This removes the SDK-owned layout that the width-cap /
// overflow fixes could only mitigate. Photosensitivity warning shown here is
// legally required (the check flashes bright colors).
export default function AWSLivenessV2({ livenessSession, vendorData }: { livenessSession: string, vendorData: any }) {
    useWakeLock();
    const { t } = useTranslation();
    const [hasStarted, setHasStarted] = useState(false);
    const [hasCameraAccessError, setHasCameraAccessError] = useState(false);

    if (!vendorData?.authentication || !vendorData?.session_id) {
        return (
            <div style={{
                width: '100%',
                minHeight: '100dvh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#f0f0f0'
            }}>
                <div style={{ textAlign: 'center', color: '#666' }}>
                    <h2>Configuration Error</h2>
                    <p>Missing required AWS liveness configuration data.</p>
                </div>
            </div>
        );
    }

    const { authentication, session_id, region } = vendorData;

    if (hasCameraAccessError) {
        return (
            <div data-testid="aws-liveness-v2-camera-access-error" className="liveness-v2-fullscreen">
                <div className="liveness-v2-modal liveness-v2-modal--instructions">
                    <div className="liveness-v2-modal__icon liveness-v2-modal__icon--camera"><CameraSlashIcon /></div>
                    <h2 className="liveness-v2-modal__title">{t('LIVENESS_V2_CAMERA_BLOCKED_TITLE')}</h2>
                    <p className="liveness-v2-modal__body">{t('LIVENESS_V2_CAMERA_BLOCKED_BODY')}</p>
                    <ol className="liveness-v2-permission-steps">
                        <li>{t('LIVENESS_V2_CAMERA_BLOCKED_STEP_ONE')}</li>
                        <li>{t('LIVENESS_V2_CAMERA_BLOCKED_STEP_TWO')}</li>
                        <li>{t('LIVENESS_V2_CAMERA_BLOCKED_STEP_THREE')}</li>
                        <li>{t('LIVENESS_V2_CAMERA_BLOCKED_STEP_FOUR')}</li>
                    </ol>
                    <button type="button" className="liveness-v2-modal__button" onClick={() => setHasCameraAccessError(false)}>
                        {t('LIVENESS_V2_CAMERA_BLOCKED_BUTTON')}
                    </button>
                </div>
            </div>
        );
    }

    if (!hasStarted) {
        return (
            <div
                data-testid="aws-liveness-v2-start-screen"
                className="liveness-v2-fullscreen"
            >
                <div className="liveness-v2-modal">
                    <div className="liveness-v2-modal__icon" aria-hidden="true">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2" />
                            <path d="M12 2v2m0 16v2M4.93 4.93l1.41 1.41m11.32 11.32 1.41 1.41M2 12h2m16 0h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </div>
                    <h2 className="liveness-v2-modal__title">
                        {t('LIVENESS_V2_PHOTOSENSITIVITY_TITLE')}
                    </h2>
                    <p className="liveness-v2-modal__body" role="note">
                        {t('LIVENESS_V2_PHOTOSENSITIVITY_BODY')}
                    </p>

                <button
                    type="button"
                    data-testid="aws-liveness-v2-start-button"
                    onClick={() => setHasStarted(true)}
                    className="liveness-v2-modal__button"
                >
                    {t('LIVENESS_V2_START_BUTTON')}
                </button>
                </div>
            </div>
        );
    }

    return (
        <div
            data-testid="aws-liveness-v2-sdk"
            style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
        >
            <LivenessQuickStartReact
                awsSessionId={session_id}
                livenessSessionId={livenessSession}
                region={region}
                disableStartScreen
                onCameraAccessError={() => setHasCameraAccessError(true)}
                credentials={{
                    accessKeyId: authentication.access_key_id,
                    secretAccessKey: authentication.secret_access_key,
                    sessionToken: authentication.session_token,
                }}
            />
        </div>
    );
}
